import { z } from 'zod';
import { NamedRefSchema, Taxi99LocationSchema } from './taxi99_wire.js';

/** Telefone em E.164. */
export const E164 = z.string().regex(/^\+\d{10,15}$/);

/** Token do link: 16 bytes aleatórios em base64url, 22 caracteres. Só o sha256 é gravado. */
export const RideLinkTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{22}$/);

export const RideBatchEndSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('fixed'), place: Taxi99LocationSchema }).strict(),
  z.object({ kind: z.literal('free') }).strict(),
]);
export type RideBatchEnd = z.infer<typeof RideBatchEndSchema>;

export const RideBatchRouteSchema = z
  .object({ from: RideBatchEndSchema, to: RideBatchEndSchema })
  .strict();
export type RideBatchRoute = z.infer<typeof RideBatchRouteSchema>;

export const RideLinkStatusSchema = z.enum([
  'sent',
  'failed',
  'opened',
  'riding',
  'done',
  'expired',
  'revoked',
]);
export type RideLinkStatus = z.infer<typeof RideLinkStatusSchema>;

export const RideBatchStateSchema = z.enum(['scheduled', 'active', 'closed']);
export type RideBatchState = z.infer<typeof RideBatchStateSchema>;

/** Como o colaborador abre o link: `app` = pelo app VOLL com login, só o dono (o padrão); `public` =
 *  sem login, o token é a credencial. Vale para o lote inteiro e não muda depois da criação. */
export const RideBatchLinkAccessSchema = z.enum(['app', 'public']);
export type RideBatchLinkAccess = z.infer<typeof RideBatchLinkAccessSchema>;

export const RideLinkAvailabilitySchema = z.enum(['open', 'not_yet', 'expired', 'revoked', 'used']);
export type RideLinkAvailability = z.infer<typeof RideLinkAvailabilitySchema>;

export const RideBatchAllocationSchema = z
  .object({
    company: NamedRefSchema,
    costCenter: NamedRefSchema,
    project: NamedRefSchema.optional(),
    motive: NamedRefSchema,
  })
  .strict();
export type RideBatchAllocation = z.infer<typeof RideBatchAllocationSchema>;

export const RideBatchWindowSchema = z
  .object({ startsAt: z.iso.datetime(), endsAt: z.iso.datetime() })
  .strict()
  .refine((w) => Date.parse(w.startsAt) < Date.parse(w.endsAt), { message: 'window_inverted' });
export type RideBatchWindow = z.infer<typeof RideBatchWindowSchema>;

/** Quem fez a ação: o gestor da sessão. Ausente num evento quando o sistema o grava sozinho. */
export const RideBatchActorSchema = z
  .object({ userId: z.string().min(1), name: z.string().min(1) })
  .strict();
export type RideBatchActor = z.infer<typeof RideBatchActorSchema>;

/** Entrega do link por WhatsApp, à parte do `status` do link. */
export const RideLinkDeliverySchema = z.enum(['pending', 'delivered', 'failed']);
export type RideLinkDelivery = z.infer<typeof RideLinkDeliverySchema>;

/** Quanto tempo um aviso recém-enviado (`sent` com a entrega `pending`) ainda é esperado: o status da
 *  Meta chega em segundos, e um `pending` mais velho (celular fora do ar, webhook perdido, fila de
 *  status desligada) não muda sozinho. A lista mostra "Enviando" dentro da janela e o reenvio em
 *  lote a respeita: um envio em voo não leva um token novo por cima. */
export const RIDE_LINK_SENDING_WINDOW_MS = 2 * 60_000;

export const RideLinkFailureReasonSchema = z.enum(['not_whatsapp', 'send_failed', 'undelivered']);
export type RideLinkFailureReason = z.infer<typeof RideLinkFailureReasonSchema>;

export const RideLinkEventTypeSchema = z.enum([
  'added',
  'sent',
  'delivered',
  'undelivered',
  'opened',
  'requested',
  'request_failed',
  'completed',
  'resent',
  'revoked',
  'reissued',
  'route_changed',
  'phone_changed',
]);
export type RideLinkEventType = z.infer<typeof RideLinkEventTypeSchema>;

/** Uma linha do histórico da pessoa. */
export const RideLinkEventSchema = z
  .object({
    type: RideLinkEventTypeSchema,
    at: z.iso.datetime(),
    by: RideBatchActorSchema.optional(),
    reason: RideLinkFailureReasonSchema.optional(),
    fare: z.number().optional(),
    from: Taxi99LocationSchema.optional(),
    to: Taxi99LocationSchema.optional(),
    /** `revoked` gravado pelo encerramento do grupo. */
    closing: z.literal(true).optional(),
  })
  .strict();
export type RideLinkEvent = z.infer<typeof RideLinkEventSchema>;

export const RideBatchPersonInputSchema = z
  .object({
    /** Id do viajante no legado (o mesmo do `BusinessIdentity.userId`). */
    userId: z.string().min(1),
    name: z.string().min(1).max(200),
    phone: E164,
    route: RideBatchRouteSchema,
  })
  .strict();

export const CreateRideBatchInputSchema = z
  .object({
    clientId: z.string().min(1),
    createdBy: RideBatchActorSchema,
    clientName: z.string().min(1),
    name: z.string().trim().min(1).max(120),
    window: RideBatchWindowSchema,
    allocation: RideBatchAllocationSchema,
    defaultRoute: RideBatchRouteSchema,
    people: z.array(RideBatchPersonInputSchema).min(1).max(500),
    linkAccess: RideBatchLinkAccessSchema.default('app'),
    /** `false` = o gestor não manda o link por WhatsApp (ele o entrega por fora). Só com o link pelo app. */
    whatsapp: z.boolean().optional(),
    /** O rascunho que este grupo concretiza: a liberação o apaga e um reenvio devolve o mesmo grupo. */
    draftId: z.string().min(1).optional(),
  })
  .strict()
  .refine((b) => b.whatsapp !== false || b.linkAccess === 'app', {
    message: 'whatsapp_requires_app',
  });
export type CreateRideBatchInput = z.infer<typeof CreateRideBatchInputSchema>;

const RideRefSchema = z
  .object({ voucherId: z.string(), requestedAt: z.iso.datetime(), fare: z.number().optional() })
  .strict();

export const RideLinkViewSchema = z
  .object({
    id: z.string(),
    userId: z.string(),
    name: z.string(),
    phone: z.string(),
    route: RideBatchRouteSchema,
    status: RideLinkStatusSchema,
    statusAt: z.iso.datetime(),
    delivery: RideLinkDeliverySchema,
    failureReason: RideLinkFailureReasonSchema.optional(),
    events: z.array(RideLinkEventSchema),
    ride: RideRefSchema.optional(),
    /** O gestor pode "Liberar de novo": `done`/`revoked`, ou `riding` há mais de 48 h sem valor, com o
     *  grupo aberto (na leitura). */
    reissuable: z.boolean(),
    /** Corridas tiradas do link por um "Liberar de novo"; o valor delas ainda conta no gasto. */
    previousRides: z.array(RideRefSchema).optional(),
  })
  .strict();
export type RideLinkView = z.infer<typeof RideLinkViewSchema>;

export const RideBatchSummarySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    window: RideBatchWindowSchema,
    state: RideBatchStateSchema,
    linkAccess: RideBatchLinkAccessSchema,
    /** Só `false` (sem aviso por WhatsApp); ausente = o link foi pelo WhatsApp. */
    whatsapp: z.literal(false).optional(),
    allocation: RideBatchAllocationSchema,
    createdBy: z.object({ userId: z.string(), name: z.string() }),
    counts: z.object({
      links: z.number(),
      opened: z.number(),
      rides: z.number(),
      failed: z.number(),
    }),
    /** Soma de `ride.fare` conhecidos, BRL. */
    spent: z.number(),
    createdAt: z.iso.datetime(),
  })
  .strict();
export type RideBatchSummary = z.infer<typeof RideBatchSummarySchema>;

export const RideBatchDetailSchema = RideBatchSummarySchema.extend({
  defaultRoute: RideBatchRouteSchema,
  links: z.array(RideLinkViewSchema),
});
export type RideBatchDetail = z.infer<typeof RideBatchDetailSchema>;

/** O que se sabe da corrida além do pedido, no que a página pública e o resolve devolvem. */
const RideOutcomeFields = {
  completedAt: z.iso.datetime().optional(),
  /** As pontas do evento `requested`. */
  from: Taxi99LocationSchema.optional(),
  to: Taxi99LocationSchema.optional(),
  fare: z.number().optional(),
};

/** O que a página pública recebe. Sem userId, sem telefone completo, sem alocação. */
export const RideLinkPublicViewSchema = z
  .object({
    status: RideLinkStatusSchema,
    availability: RideLinkAvailabilitySchema,
    clientName: z.string(),
    batchName: z.string(),
    organizerName: z.string(),
    window: RideBatchWindowSchema,
    route: RideBatchRouteSchema,
    /** "(11) 9••••-4821" */
    phoneMasked: z.string(),
    /** Só quando o grupo é `linkAccess: 'app'`. */
    firstName: z.string().optional(),
    revokedAt: z.iso.datetime().optional(),
    /** Só quando um reenvio trocou o token do link: a hora em que o link novo foi enviado. */
    replacedAt: z.iso.datetime().optional(),
    ride: z.object({ requestedAt: z.iso.datetime(), ...RideOutcomeFields }).optional(),
  })
  .strict();
export type RideLinkPublicView = z.infer<typeof RideLinkPublicViewSchema>;

/** O que o BFF precisa para criar a corrida. Só no s2s, nunca vai para o browser. */
export const RideLinkInternalSchema = z
  .object({
    id: z.string(),
    batchId: z.string(),
    clientId: z.string(),
    userId: z.string(),
    phone: z.string(),
    route: RideBatchRouteSchema,
    status: RideLinkStatusSchema,
    availability: RideLinkAvailabilitySchema,
    /** O modo do lote: o BFF decide por ele se a sessão é exigida, nunca pelo endereço. */
    linkAccess: RideBatchLinkAccessSchema,
    window: RideBatchWindowSchema,
    allocation: RideBatchAllocationSchema,
    batchName: z.string(),
    clientName: z.string(),
    organizerName: z.string(),
    /** Só quando o grupo é `linkAccess: 'app'`. */
    firstName: z.string().optional(),
    revokedAt: z.iso.datetime().optional(),
    /** Só quando um reenvio trocou o token do link: a hora em que o link novo foi enviado. */
    replacedAt: z.iso.datetime().optional(),
    ride: z
      .object({ voucherId: z.string(), requestedAt: z.iso.datetime(), ...RideOutcomeFields })
      .strict()
      .optional(),
  })
  .strict();
export type RideLinkInternal = z.infer<typeof RideLinkInternalSchema>;

/** Rascunho: tudo opcional menos o nome e a rota padrão; a liberação exige o resto. */
export const RideBatchDraftAllocationSchema = RideBatchAllocationSchema.partial();
export type RideBatchDraftAllocation = z.infer<typeof RideBatchDraftAllocationSchema>;

export const RideBatchDraftPersonSchema = z
  .object({
    userId: z.string().min(1),
    name: z.string().min(1).max(200),
    phone: E164.optional(),
    /** O número veio do cadastro (e não foi digitado): ao liberar, o cadastro continua mandando. */
    phoneFromProfile: z.boolean().optional(),
    route: RideBatchRouteSchema,
  })
  .strict();
export type RideBatchDraftPerson = z.infer<typeof RideBatchDraftPersonSchema>;

export const SaveRideBatchDraftInputSchema = z
  .object({
    clientId: z.string().min(1),
    by: RideBatchActorSchema,
    name: z.string().trim().min(1).max(120),
    window: RideBatchWindowSchema.optional(),
    allocation: RideBatchDraftAllocationSchema.default({}),
    defaultRoute: RideBatchRouteSchema,
    linkAccess: RideBatchLinkAccessSchema.default('app'),
    whatsapp: z.literal(false).optional(),
    people: z.array(RideBatchDraftPersonSchema).max(500),
  })
  .strict();
export type SaveRideBatchDraftInput = z.infer<typeof SaveRideBatchDraftInputSchema>;

export const RideBatchDraftSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    window: RideBatchWindowSchema.optional(),
    allocation: RideBatchDraftAllocationSchema,
    defaultRoute: RideBatchRouteSchema,
    linkAccess: RideBatchLinkAccessSchema,
    whatsapp: z.literal(false).optional(),
    people: z.array(RideBatchDraftPersonSchema),
    createdBy: RideBatchActorSchema,
    updatedBy: RideBatchActorSchema,
    updatedAt: z.iso.datetime(),
  })
  .strict();
export type RideBatchDraft = z.infer<typeof RideBatchDraftSchema>;

export const RideBatchDraftSummarySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    window: RideBatchWindowSchema.optional(),
    people: z.number().int(),
    updatedAt: z.iso.datetime(),
  })
  .strict();
export type RideBatchDraftSummary = z.infer<typeof RideBatchDraftSummarySchema>;

export const RideBatchListSchema = z
  .object({
    batches: z.array(RideBatchSummarySchema),
    drafts: z.array(RideBatchDraftSummarySchema),
  })
  .strict();
export type RideBatchList = z.infer<typeof RideBatchListSchema>;

/** Corpo das ações do gestor sobre um grupo ou link: de quem é o cliente e quem as fez. */
export const RideBatchActionInputSchema = z
  .object({ clientId: z.string().min(1), by: RideBatchActorSchema })
  .strict();
export type RideBatchActionInput = z.infer<typeof RideBatchActionInputSchema>;

export const ResendRideLinkInputSchema = RideBatchActionInputSchema.extend({
  phone: E164.optional(),
}).strict();
export type ResendRideLinkInput = z.infer<typeof ResendRideLinkInputSchema>;

export const UpdateRideBatchInputSchema = RideBatchActionInputSchema.extend({
  name: z.string().trim().min(1).max(120).optional(),
  allocation: RideBatchAllocationSchema.optional(),
})
  .strict()
  .refine((b) => b.name !== undefined || b.allocation !== undefined, { message: 'empty_update' });
export type UpdateRideBatchInput = z.infer<typeof UpdateRideBatchInputSchema>;

export const UpdateRideLinkInputSchema = RideBatchActionInputSchema.extend({
  route: RideBatchRouteSchema,
}).strict();
export type UpdateRideLinkInput = z.infer<typeof UpdateRideLinkInputSchema>;

export const RideLinkRideBodySchema = z
  .object({
    /** Só usado quando a ponta é livre; numa ponta fixa o BFF ignora o do corpo (não recusa). */
    from: Taxi99LocationSchema.optional(),
    to: Taxi99LocationSchema.optional(),
    observation: z.string().max(300).optional(),
  })
  .strict();
export type RideLinkRideBody = z.infer<typeof RideLinkRideBodySchema>;

/** Teto do arquivo da planilha (bytes) e das linhas de dados: o mesmo no web-chat, no BFF e no serviço. */
export const SHEET_MAX_BYTES = 2 * 1024 * 1024;
export const SHEET_MAX_ROWS = 500;

/** Uma linha de dados da planilha, crua. `line` é a linha da planilha (o cabeçalho é a 1). */
export const SheetRowSchema = z
  .object({
    line: z.number().int(),
    email: z.string(),
    phone: z.string(),
    from: z.string(),
    to: z.string(),
    error: z.literal('email_empty').optional(),
  })
  .strict();
export type SheetRow = z.infer<typeof SheetRowSchema>;

/** As recusas 400 do `POST /internal/sheets/parse`, que o BFF repassa como vieram. */
export const SheetParseErrorSchema = z.enum([
  'invalid_body',
  'unsupported_file',
  'too_large',
  'missing_email_column',
  'too_many_rows',
]);
export type SheetParseError = z.infer<typeof SheetParseErrorSchema>;

/** O resultado de cada linha na prévia: os da planilha mais as barreiras de elegibilidade e de endereço do BFF. */
export const SheetPreviewResultSchema = z.enum([
  'found',
  'found_sheet_phone',
  'duplicate',
  'email_empty',
  'not_in_base',
  'no_phone',
  'not_eligible',
  'address_not_found',
]);
export type SheetPreviewResult = z.infer<typeof SheetPreviewResultSchema>;

/** `POST /mobility/ride-batches/sheet-preview` devolve uma por linha da planilha, na ordem dela. */
export const SheetPreviewRowSchema = z
  .object({
    line: z.number().int(),
    email: z.string(),
    result: SheetPreviewResultSchema,
    /** Presentes quando o e-mail casou com um viajante do cliente. */
    userId: z.string().optional(),
    name: z.string().optional(),
    /** Em E.164: em `found`, o celular do cadastro (quando o viajante tem um que a 99 aceita); em
     *  `found_sheet_phone`, o da planilha. */
    phone: E164.optional(),
    /** Só em `found`/`found_sheet_phone`. `null` = a ponta padrão do lote. */
    route: z
      .object({ from: RideBatchEndSchema.nullable(), to: RideBatchEndSchema.nullable() })
      .strict()
      .optional(),
  })
  .strict();
export type SheetPreviewRow = z.infer<typeof SheetPreviewRowSchema>;
export const SheetPreviewResponseSchema = z
  .object({ rows: z.array(SheetPreviewRowSchema) })
  .strict();
